import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { SelectOption } from '@vchasno/ui-kit';

import {
  DocumentActionType,
  ResponseModelVacationTaskTemplateRetrieveSerializer,
} from 'api/api';

import { VacationTaskDocumentType } from './vacationTaskTemplate.types';
import {
  buildDocumentActionState,
  buildInitialVacationTaskTemplateState,
  parseVacationTaskTemplate,
  toggleDocumentAccessSettings,
} from './vacationTaskTemplate.utils';

export const vacationTaskTemplateSlice = createSlice({
  name: 'vacationTaskTemplate',
  initialState: buildInitialVacationTaskTemplateState(),
  reducers: {
    toggleDocumentsSection: (state) => {
      if (state.section) {
        state.section = null;
      } else {
        state.section = 'documents';
      }
    },
    changeManager: (state, action: PayloadAction<SelectOption>) => {
      state.managerId = action.payload.value || null;
    },
    setActiveDocument: (
      state,
      action: PayloadAction<VacationTaskDocumentType>,
    ) => {
      state.activeDocument = action.payload;
    },
    addRequestAction: (state, action: PayloadAction<SelectOption>) => {
      state.documents.request.actions.push(
        buildDocumentActionState(DocumentActionType.Approve, action.payload),
      );
    },
    removeRequestAction: (state, action: PayloadAction<number>) => {
      state.documents.request.actions.splice(action.payload, 1);
    },
    toggleRequestActive: (state) => {
      state.documents.request.isActive = !state.documents.request.isActive;
    },
    toggleApplicationDocumentAccess: (state) => {
      state.documents.application.accessSettings = toggleDocumentAccessSettings(
        state.documents.application.accessSettings,
      );
    },
    addApplicationAction: (state, action: PayloadAction<SelectOption>) => {
      state.documents.application.actions.push(
        buildDocumentActionState(DocumentActionType.Sign, action.payload),
      );
    },
    removeApplicationAction: (state, action: PayloadAction<number>) => {
      state.documents.application.actions.splice(action.payload, 1);
    },
    toggleApplicationActionType: (state, action: PayloadAction<number>) => {
      const currentType =
        state.documents.application.actions[action.payload].type;

      if (currentType === DocumentActionType.Sign) {
        state.documents.application.actions[action.payload].type =
          DocumentActionType.View;
      } else {
        state.documents.application.actions[action.payload].type =
          DocumentActionType.Sign;
      }
    },
    reorderApplicationActions: (state, action: PayloadAction<number[]>) => {
      state.documents.application.actions = action.payload.map(
        (index) => state.documents.application.actions[index],
      );
    },
    toggleApplicationActive: (state) => {
      state.documents.application.isActive =
        !state.documents.application.isActive;
    },
    toggleApplicationIsParallel: (state) => {
      state.documents.application.isParallel =
        !state.documents.application.isParallel;
    },
    toggleOrderDocumentAccess: (state) => {
      state.documents.order.accessSettings = toggleDocumentAccessSettings(
        state.documents.order.accessSettings,
      );
    },
    addOrderAction: (state, action: PayloadAction<SelectOption>) => {
      state.documents.order.actions.push(
        buildDocumentActionState(DocumentActionType.Sign, action.payload),
      );
    },
    removeOrderAction: (state, action: PayloadAction<number>) => {
      state.documents.order.actions.splice(action.payload, 1);
    },
    toggleOrderActionType: (state, action: PayloadAction<number>) => {
      const currentType = state.documents.order.actions[action.payload].type;

      if (currentType === DocumentActionType.Sign) {
        state.documents.order.actions[action.payload].type =
          DocumentActionType.View;
      } else {
        state.documents.order.actions[action.payload].type =
          DocumentActionType.Sign;
      }
    },
    reorderOrderActions: (state, action: PayloadAction<number[]>) => {
      state.documents.order.actions = action.payload.map(
        (index) => state.documents.order.actions[index],
      );
    },
    toggleOrderActive: (state) => {
      state.documents.order.isActive = !state.documents.order.isActive;
    },
    toggleOrderIsParallel: (state) => {
      state.documents.order.isParallel = !state.documents.order.isParallel;
    },
    setInitialState: (
      _,
      action: PayloadAction<ResponseModelVacationTaskTemplateRetrieveSerializer>,
    ) => parseVacationTaskTemplate(action.payload),
    resetState: () => buildInitialVacationTaskTemplateState(),
  },
  selectors: {
    section: (state) => state.section,
    activeDocument: (state) => state.activeDocument,
    type: (state) => state.type,
    documents: (state) => state.documents,
    request: (state) => state.documents.request,
    application: (state) => state.documents.application,
    order: (state) => state.documents.order,
    requestActions: (state) => state.documents.request.actions,
    applicationActions: (state) => state.documents.application.actions,
    orderActions: (state) => state.documents.order.actions,
    managerId: (state) => state.managerId,
  },
});
